import { ReactNode, useState } from 'react'
import { Box, CircularProgress, Stack, Tab, Tabs } from '@mui/material'
import { com } from '@eidu/web'
import EntityPicker from '../../../../components/input/EntityPicker'
import RegistryAssetPicker from './RegistryAssetPicker'
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityTypeKind = com.eidu.sharedlib.entity.type.EntityTypeKind
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString

type PickerSource = 'org' | 'registry'

type EntityPickerWithRegistryProps = {
  value: EntityId | undefined
  onChange: (id: EntityId | undefined) => void
  kindFilter: (kind: EntityTypeKind) => boolean
  registryKind?: string
  selectedVersionId?: string
  onRegistryVersionSelect?: (versionId: string) => void
  localPickerExtras?: ReactNode
  hasLocalTypes?: boolean
  valueSource?: PickerSource
  resolvingValueSource?: boolean
  typeLabel?: string
  entityLabel?: string
  noOptionsText?: string
}

const EntityPickerWithRegistry = ({
  value,
  onChange,
  kindFilter,
  registryKind,
  selectedVersionId,
  onRegistryVersionSelect,
  localPickerExtras,
  hasLocalTypes = true,
  valueSource,
  resolvingValueSource = false,
  typeLabel,
  entityLabel,
  noOptionsText,
}: EntityPickerWithRegistryProps) => {
  const [userTab, setUserTab] = useState<PickerSource | undefined>(undefined)
  const tab: PickerSource = userTab ?? valueSource ?? (hasLocalTypes ? 'org' : 'registry')

  const onRegistrySelect = (entityIdString: string, versionId: string) => {
    onChange(entityIdFromString(entityIdString))
    onRegistryVersionSelect?.(versionId)
  }

  const showSpinner = resolvingValueSource && userTab === undefined

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 528 }}>
      <Tabs value={tab} onChange={(_, v) => setUserTab(v as PickerSource)} sx={{ mb: 2, flexShrink: 0 }}>
        {hasLocalTypes && <Tab label="This organization" value="org" />}
        <Tab label="Registry (cross-org)" value="registry" />
      </Tabs>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {showSpinner ? (
          <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Stack>
        ) : tab === 'org' ? (
          <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            <EntityPicker
              value={value}
              onChange={onChange}
              kindFilter={kindFilter}
              typeLabel={typeLabel}
              entityLabel={entityLabel}
              noOptionsText={noOptionsText}
            />
            {localPickerExtras}
          </Stack>
        ) : (
          <RegistryAssetPicker
            kind={registryKind}
            selectedEntityId={value?.toString()}
            selectedVersionId={selectedVersionId}
            onSelect={onRegistrySelect}
          />
        )}
      </Box>
    </Box>
  )
}

export default EntityPickerWithRegistry
